

import { useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import {
  CircleNotchIcon,
  UsersIcon,
  WarningCircleIcon,
  XIcon,
} from '@phosphor-icons/react'
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { memberApi } from "@/lib/api/members.api";
import { Member, TimeEntryTag, TimeEntryTagStatusEnum } from "@scrambl/shared";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useActiveTagsForTimeEntry, useReplaceTagsForTimeEntry } from "@/hooks/useTimeEntryTags";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { createLogger } from '@/lib/logger';

const logger = createLogger('components/time-entry/TimeEntryTagging');

const DEBOUNCE_MS = 500;
const EMPTY_EXCLUDED_USER_IDS: string[] = [];
/** Matches the dropdown's max-h-60; the inline style narrows it near a viewport edge. */
const DROPDOWN_MAX_HEIGHT_PX = 240;
const DROPDOWN_MIN_HEIGHT_PX = 120;
const VIEWPORT_MARGIN_PX = 8;

function areSetsEqual<T>(left: Set<T>, right: Set<T>): boolean {
  return left.size === right.size && [...left].every(value => right.has(value));
}

export interface TimeEntryTaggingProps {
  timeEntryId?: string;
  label?: ReactNode;
  className?: string;
  disabled?: boolean;
  limit?: number;
  portalToBody?: boolean;
  selectedUserIds?: string[];
  onSelectedUserIdsChange?: (userIds: string[]) => void;
  hideHelperText?: boolean;
  excludedUserIds?: string[];
  /**
   * Close the dropdown when the page scrolls. Off for surfaces that live inside
   * their own scroll container (the split dialog), where scrolling to read the
   * rest of the list would otherwise dismiss it. Mirrors LinkWorkSelector.
   */
  closeOnScroll?: boolean;
}

interface TaggedMember {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  role: string;
  tagId?: string;
  status?: TimeEntryTagStatusEnum;
}

export function TimeEntryTagging({
  timeEntryId,
  label = "Tag Members (Optional)",
  className,
  disabled = false,
  limit = 50,
  portalToBody = true,
  selectedUserIds,
  onSelectedUserIdsChange,
  hideHelperText = false,
  excludedUserIds = EMPTY_EXCLUDED_USER_IDS,
  closeOnScroll = true,
}: TimeEntryTaggingProps) {
  const activeTimeEntryId = timeEntryId ?? "";
  const isServerBacked = !!activeTimeEntryId;
  const normalizedExcludedUserIds = useMemo(
    () => excludedUserIds.filter(Boolean),
    [excludedUserIds],
  );
  const excludedUserIdSet = useMemo(
    () => new Set(normalizedExcludedUserIds),
    [normalizedExcludedUserIds],
  );
  const initialSelectedUserIds =
    selectedUserIds?.filter(id => !excludedUserIdSet.has(id)) ?? [];
  const [search, setSearch] = useState("");
  const [allMembers, setAllMembers] = useState<TaggedMember[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [hasFetchedMembers, setHasFetchedMembers] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number; maxHeight: number } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Local optimistic selection state for instant UI
  const [localSelectedIds, setLocalSelectedIds] = useState<Set<string>>(
    () => new Set(initialSelectedUserIds)
  );
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestIdsRef = useRef<Set<string>>(new Set(initialSelectedUserIds));
  // Tracks the last set of IDs we sent to the server.  While set, the
  // server sync effect will refuse to overwrite local state — preventing
  // stale refetch responses from causing visual flicker.  Cleared once
  // the server response matches what we sent (confirming it's current).
  const lastSentIdsRef = useRef<Set<string> | null>(null);

  const omitExcludedUserIds = (ids: Iterable<string>) => {
    const next = new Set<string>();
    for (const id of ids) {
      if (!excludedUserIdSet.has(id)) {
        next.add(id);
      }
    }
    return next;
  };

  // Get current tags for this time entry
  const { data: tagsResponse, isLoading: isLoadingTags, error: tagsError } = useActiveTagsForTimeEntry(activeTimeEntryId);
  const replaceTagsMutation = useReplaceTagsForTimeEntry();
  const mutateReplaceTags = replaceTagsMutation.mutate;
  const isReplaceTagsPending = replaceTagsMutation.isPending;
  const isReplaceTagsError = replaceTagsMutation.isError;

  // Convert TimeEntryTag objects to TaggedMember format
  const serverTags = useMemo<TaggedMember[]>(() => {
    if (!isServerBacked) return [];
    return (
      tagsResponse?.data
        ?.map((tag: TimeEntryTag) => ({
          id: tag.taggedUserId,
          name:
            tag.taggedUser?.displayName ||
            `${tag.taggedUser?.firstName} ${tag.taggedUser?.lastName}`.trim() ||
            tag.taggedUser?.email ||
            'Unknown User',
          email: tag.taggedUser?.email || '',
          avatar: undefined,
          role: 'member',
          tagId: tag.id,
          status: tag.status,
        }))
        .filter(tag => !excludedUserIdSet.has(tag.id)) || []
    );
  }, [isServerBacked, tagsResponse?.data, excludedUserIdSet]);

  // In local mode (new unsaved entry), keep local selection in sync with parent.
  useEffect(() => {
    if (isServerBacked) return;
    const nextIds = new Set(
      (selectedUserIds ?? []).filter(id => !excludedUserIdSet.has(id)),
    );
    setLocalSelectedIds(prev => {
      if (areSetsEqual(prev, nextIds)) {
        latestIdsRef.current = prev;
        return prev;
      }
      latestIdsRef.current = nextIds;
      return nextIds;
    });
  }, [isServerBacked, selectedUserIds, excludedUserIdSet]);

  // Sync local state from server data.
  // - While the dropdown is open, local state is the source of truth.
  // - After we send changes, we keep local state until the server
  //   response matches what we sent (proving the data is fresh).
  //   This prevents stale refetch responses from causing flicker.
  useEffect(() => {
    if (!isServerBacked) return;
    if (showDropdown) return;

    const serverIds = new Set(serverTags.map(m => m.id));

    if (lastSentIdsRef.current) {
      // On mutation error, clear tracking so we revert to server state.
      if (isReplaceTagsError && !isReplaceTagsPending) {
        lastSentIdsRef.current = null;
        // Fall through to server sync below
      } else {
        if (areSetsEqual(serverIds, lastSentIdsRef.current)) {
          // Server confirmed our changes — clear tracking.
          // Local state already matches so no update needed.
          lastSentIdsRef.current = null;
        }
        // Either server matches (no-op) or is stale (keep local).
        return;
      }
    }

    // No pending outbound changes — server data is authoritative.
    setLocalSelectedIds(prev => {
      if (areSetsEqual(prev, serverIds)) {
        latestIdsRef.current = prev;
        return prev;
      }
      latestIdsRef.current = serverIds;
      return serverIds;
    });
  }, [isServerBacked, serverTags, showDropdown, isReplaceTagsError, isReplaceTagsPending]);

  // Build display-ready list of selected members using allMembers for full info
  const allMembersById = (() => {
    const map = new Map<string, TaggedMember>();
    for (const m of allMembers) map.set(m.id, m);
    for (const m of serverTags) map.set(m.id, m);
    return map;
  })();

  const displayedTags = (() => {
    return Array.from(localSelectedIds)
      .filter(id => !excludedUserIdSet.has(id))
      .map(id => allMembersById.get(id))
      .filter((m): m is TaggedMember => m !== undefined);
  })();

  // Schedule debounced API sync
  const syncSelection = (newIds: Set<string>) => {
    const sanitizedIds = omitExcludedUserIds(newIds);
    latestIdsRef.current = sanitizedIds;
    if (!isServerBacked) {
      onSelectedUserIdsChange?.(Array.from(sanitizedIds));
      return;
    }
    lastSentIdsRef.current = new Set(sanitizedIds);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      mutateReplaceTags({
        timeEntryId: activeTimeEntryId,
        data: { taggedUserIds: Array.from(sanitizedIds) },
      });
    }, DEBOUNCE_MS);
  };

  // Flush pending debounce when dropdown closes
  useEffect(() => {
    if (!isServerBacked) return;
    if (!showDropdown && debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
      lastSentIdsRef.current = new Set(latestIdsRef.current);
      mutateReplaceTags({
        timeEntryId: activeTimeEntryId,
        data: { taggedUserIds: Array.from(latestIdsRef.current) },
      });
    }
  }, [isServerBacked, showDropdown, activeTimeEntryId, mutateReplaceTags]);

  // Cleanup on unmount — flush pending changes
  useEffect(() => {
    return () => {
      if (!isServerBacked) return;
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
        lastSentIdsRef.current = new Set(latestIdsRef.current);
        mutateReplaceTags({
          timeEntryId: activeTimeEntryId,
          data: { taggedUserIds: Array.from(latestIdsRef.current) },
        });
      }
    };
  }, [isServerBacked, activeTimeEntryId, mutateReplaceTags]);

  // Calculate dropdown position. The field is often near the bottom of a
  // scrollable dialog, so anchoring blindly below it pushes the list off the
  // viewport where its own overflow scroll can't reach — flip above (or cap the
  // height) whenever the space below is too tight.
  const updateDropdownPosition = () => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN_PX;
    const spaceAbove = rect.top - VIEWPORT_MARGIN_PX;
    const openAbove = spaceBelow < DROPDOWN_MAX_HEIGHT_PX && spaceAbove > spaceBelow;
    const maxHeight = Math.max(
      DROPDOWN_MIN_HEIGHT_PX,
      Math.min(DROPDOWN_MAX_HEIGHT_PX, openAbove ? spaceAbove : spaceBelow),
    );
    const width = Math.min(rect.width, window.innerWidth - VIEWPORT_MARGIN_PX * 2);
    setDropdownPosition({
      top: openAbove ? rect.top - 4 - maxHeight : rect.bottom + 4,
      left: Math.max(
        VIEWPORT_MARGIN_PX,
        Math.min(rect.left, window.innerWidth - width - VIEWPORT_MARGIN_PX),
      ),
      width,
      maxHeight,
    });
  };

  // Fetch all members when dropdown first opens
  const fetchAllMembers = async () => {
    if (hasFetchedMembers) return;
    setIsLoadingMembers(true);
    try {
      const response = await memberApi.getMembersForDisplay({
        limit,
        status: 'ACTIVE',
      });
      const members: TaggedMember[] = response.data
        .filter((member: Member) => !excludedUserIdSet.has(member.userId))
        .map((member: Member) => ({
          id: member.userId,
          name:
            member.user?.displayName ||
            `${member.user?.firstName} ${member.user?.lastName}`.trim() ||
            member.user?.email ||
            'Unknown User',
          email: member.user?.email || '',
          avatar: member.user?.image || undefined,
          role: member.role,
        }));
      setAllMembers(members);
      setHasFetchedMembers(true);
    } catch (error) {
      logger.error('[TimeEntryTagging] Fetch members error:', error);
    } finally {
      setIsLoadingMembers(false);
    }
  };

  // Close dropdown when clicking outside or scrolling
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current && !containerRef.current.contains(target)) {
        const dropdownElement = document.querySelector('[data-member-tagging-dropdown]');
        if (!dropdownElement || !dropdownElement.contains(target)) {
          setShowDropdown(false);
          setSearch("");
        }
      }
    };

    const handleScroll = (event: Event) => {
      const target = event.target as Element;
      const dropdownElement = document.querySelector('[data-member-tagging-dropdown]');
      if (dropdownElement && dropdownElement.contains(target)) {
        return;
      }
      // Follow the anchor instead of dismissing: the field itself scrolls with
      // the dialog, so closing here reads as "the list won't let me scroll".
      if (!closeOnScroll) {
        if (portalToBody) updateDropdownPosition();
        return;
      }
      setShowDropdown(false);
      setSearch("");
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [showDropdown, closeOnScroll, portalToBody]);

  // Recalculate dropdown position when tags change (container height changes)
  useEffect(() => {
    if (showDropdown) {
      if (!portalToBody) return;
      updateDropdownPosition();
    }
  }, [localSelectedIds, showDropdown, portalToBody]);

  // Re-anchor when the viewport changes size (rotation, keyboard, window drag)
  useEffect(() => {
    if (!showDropdown || !portalToBody) return;
    const handleResize = () => updateDropdownPosition();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [showDropdown, portalToBody]);

  // Filter members for dropdown display (preserve original order)
  const filteredMembers = (() => {
    const searchLower = search.toLowerCase().trim();
    if (!searchLower) return allMembers;
    return allMembers.filter(m =>
      m.name.toLowerCase().includes(searchLower) || m.email.toLowerCase().includes(searchLower)
    );
  })();

  const isAllSelected = filteredMembers.length > 0 && filteredMembers.every(m => localSelectedIds.has(m.id));

  const handleToggleMember = (member: TaggedMember) => {
    setLocalSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(member.id)) {
        next.delete(member.id);
      } else {
        next.add(member.id);
      }
      syncSelection(next);
      return next;
    });
  };

  const handleSelectAll = () => {
    // Capture stable values from the closure (search text & member list
    // don't change during rapid selection clicks).
    const searchLower = search.toLowerCase().trim();
    const filterBySearch = (member: TaggedMember) => {
      if (!searchLower) return true;
      return member.name.toLowerCase().includes(searchLower) || member.email.toLowerCase().includes(searchLower);
    };
    const filteredMembers = allMembers.filter(filterBySearch);

    setLocalSelectedIds(prev => {
      const next = new Set(prev);
      // Recompute selected/unselected from `prev` so we never rely on a
      // stale render-time snapshot of localSelectedIds.
      const prevUnselected = filteredMembers.filter(m => !prev.has(m.id));
      const allSelected = filteredMembers.length > 0 && prevUnselected.length === 0;

      if (allSelected) {
        for (const m of filteredMembers) next.delete(m.id);
      } else {
        for (const m of prevUnselected) next.add(m.id);
      }
      syncSelection(next);
      return next;
    });
  };

  const handleRemoveTag = (memberId: string) => {
    setLocalSelectedIds(prev => {
      const next = new Set(prev);
      next.delete(memberId);
      syncSelection(next);
      return next;
    });
  };

  const handleOpenDropdown = () => {
    if (disabled) return;
    fetchAllMembers();
    setShowDropdown(true);
    if (portalToBody) {
      updateDropdownPosition();
    }
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      setShowDropdown(false);
      setSearch("");
    } else if (e.key === 'Backspace' && search === "" && displayedTags.length > 0) {
      handleRemoveTag(displayedTags[displayedTags.length - 1].id);
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (isServerBacked && tagsError) {
    return (
      <div className={cn("space-y-2", className)}>
        {label && <div className="text-sm font-medium">{label}</div>}
        <Alert variant="destructive">
          <WarningCircleIcon className="h-4 w-4" />
          <AlertDescription>
            Failed to load tags. Please try refreshing the page.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className={cn("space-y-2", className)}>
      {label && (
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium">{label}</div>
          {displayedTags.length > 0 && (
            <span className="text-xs text-muted-foreground">
              {displayedTags.length} {displayedTags.length === 1 ? 'member' : 'members'} tagged
            </span>
          )}
        </div>
      )}

      <div ref={containerRef} className="relative">
        {/* Tagged members and search input */}
        <div
          className={cn(
            "min-h-[42px] p-2 border border-border rounded-md bg-background cursor-text focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
            isLoadingTags && "opacity-50"
          )}
          onClick={handleOpenDropdown}
        >
          <div className="flex flex-wrap gap-1">
            {/* Loading indicator for initial tags load */}
            {isLoadingTags && displayedTags.length === 0 && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <CircleNotchIcon className="h-4 w-4 animate-spin" />
                <span className="text-sm">Loading tags...</span>
              </div>
            )}

            {/* Tagged member badges */}
            {displayedTags.map((member) => (
              <Badge
                key={member.id}
                variant="secondary"
                className="flex items-center gap-1.5 pr-1 pl-2 py-1"
              >
                <Avatar className="h-4 w-4">
                  <AvatarImage src={member.avatar} />
                  <AvatarFallback className="text-[10px]">
                    {getInitials(member.name)}
                  </AvatarFallback>
                </Avatar>
                <span className="text-xs font-medium">{member.name}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-4 w-4 p-0 hover:bg-destructive/20"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveTag(member.id);
                  }}
                  disabled={disabled}
                >
                  <XIcon className="h-3 w-3" />
                </Button>
              </Badge>
            ))}

            {/* Search input */}
            {!isLoadingTags && (
              <div className="flex-1 min-w-[120px] relative">
                <div className="flex items-center gap-1">
                  <input
                    ref={inputRef}
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onFocus={handleOpenDropdown}
                    onKeyDown={handleKeyDown}
                    placeholder={displayedTags.length === 0 ? "Search and tag members..." : "Add more..."}
                    disabled={disabled}
                    className="w-full border-0 bg-transparent p-0 h-6 text-sm outline-none placeholder:text-muted-foreground/60 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                </div>
                {isLoadingMembers && (
                  <CircleNotchIcon className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 animate-spin text-muted-foreground" />
                )}
              </div>
            )}
          </div>
        </div>

        {/* Multi-select dropdown */}
        {showDropdown && (
          portalToBody ? (
            dropdownPosition && typeof document !== 'undefined' && createPortal(
          <div
            data-member-tagging-dropdown
            className="rounded-lg border shadow-lg bg-white dark:bg-slate-900 overflow-auto p-1"
            style={{
              position: 'fixed',
              zIndex: 99999,
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
              maxHeight: `${dropdownPosition.maxHeight}px`,
              pointerEvents: 'auto',
            }}
            onMouseDown={(e) => e.stopPropagation()}
            // Portalled out of the dialog, so the dialog's scroll lock would
            // otherwise cancel every wheel/touch over this list.
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            {isLoadingMembers ? (
              <div className="p-3 flex items-center justify-center gap-2 text-muted-foreground">
                <CircleNotchIcon className="h-4 w-4 animate-spin" />
                <span className="text-sm">Loading members...</span>
              </div>
            ) : filteredMembers.length === 0 && search.length > 0 ? (
              <div className="p-3 text-center text-muted-foreground">
                <div className="mb-1">No members found</div>
                <div className="text-xs opacity-70">
                  Try searching by name or email address
                </div>
              </div>
            ) : (
              <>
                {/* Select All / Deselect All option */}
                {filteredMembers.length > 0 && (
                  <button
                    type="button"
                    className={cn(
                      "w-full flex items-center gap-2.5 px-3 py-2 rounded-md cursor-pointer text-left border-b border-border mb-1",
                      "text-sm transition-all duration-150",
                      "hover:bg-slate-100 dark:hover:bg-slate-800",
                      isAllSelected
                        ? "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 font-medium"
                        : "text-slate-700 dark:text-slate-300"
                    )}
                    onClick={handleSelectAll}
                  >
                    <div className={cn(
                      "h-4 w-4 rounded flex-shrink-0 flex items-center justify-center transition-all duration-150",
                      "border-2",
                      isAllSelected
                        ? "bg-blue-500 border-blue-500 text-white shadow-sm"
                        : "border-slate-300 dark:border-slate-600"
                    )}>
                      {isAllSelected && (
                        <svg className="h-2.5 w-2.5" fill="none" viewBox="0 0 12 12">
                          <path d="M10 3L4.5 8.5 2 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <UsersIcon className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">
                        {isAllSelected ? "Deselect All Members" : "Select All Members"}
                      </span>
                    </div>
                  </button>
                )}

                {/* Members list (stable order) */}
                {filteredMembers.map((member) => (
                  <MemberRow
                    key={member.id}
                    member={member}
                    isSelected={localSelectedIds.has(member.id)}
                    getInitials={getInitials}
                    onToggle={() => handleToggleMember(member)}
                  />
                ))}
              </>
            )}
          </div>,
          document.body
        )) : (
          <div
            data-member-tagging-dropdown
            className="absolute left-0 right-0 top-full mt-1 z-50 rounded-lg border shadow-lg bg-white dark:bg-slate-900 max-h-60 overflow-auto p-1"
            onMouseDown={(e) => e.stopPropagation()}
          >
            {isLoadingMembers ? (
              <div className="p-3 flex items-center justify-center gap-2 text-muted-foreground">
                <CircleNotchIcon className="h-4 w-4 animate-spin" />
                <span className="text-sm">Loading members...</span>
              </div>
            ) : filteredMembers.length === 0 && search.length > 0 ? (
              <div className="p-3 text-center text-muted-foreground">
                <div className="mb-1">No members found</div>
                <div className="text-xs opacity-70">
                  Try searching by name or email address
                </div>
              </div>
            ) : (
              <>
                {/* Select All / Deselect All option */}
                {filteredMembers.length > 0 && (
                  <button
                    type="button"
                    className={cn(
                      "w-full flex items-center gap-2.5 px-3 py-2 rounded-md cursor-pointer text-left border-b border-border mb-1",
                      "text-sm transition-all duration-150",
                      "hover:bg-slate-100 dark:hover:bg-slate-800",
                      isAllSelected
                        ? "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 font-medium"
                        : "text-slate-700 dark:text-slate-300"
                    )}
                    onClick={handleSelectAll}
                  >
                    <div className={cn(
                      "h-4 w-4 rounded flex-shrink-0 flex items-center justify-center transition-all duration-150",
                      "border-2",
                      isAllSelected
                        ? "bg-blue-500 border-blue-500 text-white shadow-sm"
                        : "border-slate-300 dark:border-slate-600"
                    )}>
                      {isAllSelected && (
                        <svg className="h-2.5 w-2.5" fill="none" viewBox="0 0 12 12">
                          <path d="M10 3L4.5 8.5 2 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <UsersIcon className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">
                        {isAllSelected ? "Deselect All Members" : "Select All Members"}
                      </span>
                    </div>
                  </button>
                )}

                {/* Members list (stable order) */}
                {filteredMembers.map((member) => (
                  <MemberRow
                    key={member.id}
                    member={member}
                    isSelected={localSelectedIds.has(member.id)}
                    getInitials={getInitials}
                    onToggle={() => handleToggleMember(member)}
                  />
                ))}
              </>
            )}
          </div>
        )
        )}
      </div>

      {/* Helper text */}
      {!hideHelperText && displayedTags.length === 0 && !isLoadingTags && (
        <p className="text-xs text-muted-foreground">
          Tag team members to notify them about this time entry for potential replication
        </p>
      )}
    </div>
  );
}

/** Individual member row in the dropdown */
function MemberRow({
  member,
  isSelected,
  getInitials,
  onToggle,
}: {
  member: TaggedMember;
  isSelected: boolean;
  getInitials: (name: string) => string;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      className={cn(
        "w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-left mb-1",
        "text-sm transition-all duration-150",
        "hover:bg-slate-100 dark:hover:bg-slate-800",
        isSelected
          ? "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 font-medium"
          : "text-slate-700 dark:text-slate-300"
      )}
      onClick={onToggle}
    >
      <div className={cn(
        "h-4 w-4 rounded flex-shrink-0 flex items-center justify-center transition-all duration-150",
        "border-2",
        isSelected
          ? "bg-blue-500 border-blue-500 text-white shadow-sm"
          : "border-slate-300 dark:border-slate-600"
      )}>
        {isSelected && (
          <svg className="h-2.5 w-2.5" fill="none" viewBox="0 0 12 12">
            <path d="M10 3L4.5 8.5 2 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </div>
      <Avatar className="h-7 w-7">
        <AvatarImage src={member.avatar} />
        <AvatarFallback className="text-xs">
          {getInitials(member.name)}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="truncate text-sm">{member.name}</div>
        <div className="text-xs text-muted-foreground truncate">
          {member.email}
        </div>
      </div>
    </button>
  );
}
